import { createRootRoute, Outlet, retainSearchParams } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";
import { EmbedMeta } from "../shared/components/embed-meta";
import { NotFoundPage } from "../shared/components/not-found-page";
import { RouterErrorComponent } from "../shared/components/router-error-component";
import { globalLoader } from "../shared/utils/global-loaders";

const searchSchema = z.object({
  embed: z
    .union([z.boolean(), z.string()])
    .optional()
    .transform((val) => {
      if (typeof val === "string") {
        return val === "true";
      }
      return val;
    }),
  colorScheme: z.enum(["light", "dark"]).optional(),
  width: z
    .union([z.string(), z.number()])
    .transform((val) => {
      // If it's already a number, validate it directly
      if (typeof val === "number") {
        return val >= 320 && val <= 1000 ? val : undefined;
      }
      // If it's a string, check for preset values first
      if (val === "mobile" || val === "tablet") {
        return val;
      }
      // Try to parse as number
      const num = Number.parseInt(val, 10);
      if (!Number.isNaN(num) && num >= 320 && num <= 1000) {
        return num;
      }
      // Return undefined for invalid values
      return undefined;
    })
    .optional(),
  height: z
    .union([z.string(), z.number()])
    .transform((val) => {
      // If it's already a number, validate it directly
      if (typeof val === "number") {
        return val >= 480 && val <= 1200 ? val : undefined;
      }
      // If it's a string, check for preset values first
      if (val === "mobile" || val === "tablet") {
        return val;
      }
      // Try to parse as number
      const num = Number.parseInt(val, 10);
      if (!Number.isNaN(num) && num >= 480 && num <= 1200) {
        return num;
      }
      // Return undefined for invalid values
      return undefined;
    })
    .optional(),
});

function RootNotFoundPage() {
  return <NotFoundPage fullHeight />;
}

export const Route = createRootRoute({
  loader: globalLoader,
  validateSearch: zodValidator(searchSchema),
  search: {
    middlewares: [retainSearchParams(true)],
  },
  component: RootComponent,
  notFoundComponent: RootNotFoundPage,
  errorComponent: ({ error, reset }) => {
    return <RouterErrorComponent error={error} reset={reset} />;
  },
});

function RootComponent() {
  return (
    <>
      <EmbedMeta />
      <Outlet />
    </>
  );
}
